import { CheckCircle, Circle, ArrowRight, XCircle, Spinner, User, MapPin, Wrench } from '@phosphor-icons/react';

const FLOW_STEPS = [
  { key: 'submitted', label: 'Submitted', responsibleRole: 'Agent', icon: Circle },
  { key: 'picked_up', label: 'Picked Up', responsibleRole: 'Agent', icon: User },
  { key: 'approved', label: 'Approved', responsibleRole: 'FOC', icon: CheckCircle },
  { key: 'assigned', label: 'Assigned', responsibleRole: 'Technician', icon: MapPin },
  { key: 'in_progress', label: 'In Progress', responsibleRole: 'Technician', icon: Wrench },
  { key: 'completed', label: 'Completed', responsibleRole: 'System', icon: CheckCircle },
  { key: 'closed', label: 'Closed', responsibleRole: null, icon: CheckCircle },
];

const STATUS_ORDER = {
  submitted: 0,
  picked_up: 1,
  approved: 2,
  rejected: -1,
  assigned: 3,
  in_progress: 4,
  completed: 5,
  closed: 6,
};

/**
 * Visual ticket flow stepper showing:
 * - ✅ Completed steps (green)
 * - 🔵 Current active step (blue pulse)
 * - ⚪ Future steps (grey)
 * - ❌ Rejected branch (red)
 */
export const TicketFlowStepper = ({ ticket }) => {
  if (!ticket) return null;

  const currentStatus = ticket.status;
  const currentIdx = STATUS_ORDER[currentStatus] ?? 0;
  const isRejected = currentStatus === 'rejected';

  // Find event timestamps and actor names for each step
  const eventMap = {};
  if (ticket.events) {
    for (const event of ticket.events) {
      if (event.to_status) {
        eventMap[event.to_status] = {
          timestamp: event.created_at,
          actorRole: event.actor_role,
          note: event.note,
        };
      }
    }
  }

  // Determine who is responsible now
  const getResponsibleLabel = () => {
    if (isRejected) return null;
    switch (currentStatus) {
      case 'submitted':
        return 'Waiting for: Agent pickup';
      case 'picked_up':
        return `Agent: ${ticket.agent_name || 'reviewing'}`;
      case 'approved':
        return 'Waiting for: FOC assignment';
      case 'assigned':
        return `Technician: ${ticket.tech_name || 'assigned'}`;
      case 'in_progress':
        return `Technician: ${ticket.tech_name || 'on-site'}`;
      case 'completed':
        return 'Pending auto-closure';
      case 'closed':
      default:
        return null;
    }
  };

  const responsibleLabel = getResponsibleLabel();

  return (
    <div className="space-y-4">
      {/* Current responsibility / status banner */}
      {currentStatus === 'closed' ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100 animate-fade-in">
          <CheckCircle size={16} className="text-emerald-500" weight="fill" />
          <span className="text-xs font-bold text-emerald-700">Ticket Resolved & Closed</span>
        </div>
      ) : isRejected ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-100 animate-fade-in">
          <XCircle size={16} className="text-rose-500" weight="fill" />
          <span className="text-xs font-bold text-rose-700">Ticket Rejected</span>
        </div>
      ) : responsibleLabel ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
          <Spinner size={14} className="text-blue-500 animate-spin" />
          <span className="text-xs font-bold text-blue-700">{responsibleLabel}</span>
        </div>
      ) : null}

      {/* Stepper */}
      <div className="flex items-start gap-0 overflow-x-auto pb-2">
        {FLOW_STEPS.map((step, idx) => {
          const stepIdx = STATUS_ORDER[step.key];
          const isCompleted = !isRejected && (stepIdx < currentIdx || (currentStatus === 'closed' && stepIdx === currentIdx));
          const isCurrent = !isRejected && stepIdx === currentIdx && currentStatus !== 'closed';
          const isFuture = !isRejected && stepIdx > currentIdx;
          const StepIcon = step.icon;

          // Skip picked_up step if ticket went directly submitted → approved
          if (step.key === 'picked_up' && !eventMap['picked_up'] && currentStatus !== 'picked_up') {
            return null;
          }

          const eventInfo = eventMap[step.key];

          return (
            <div key={step.key} className="flex items-start">
              {idx > 0 && (
                <div className={`w-6 h-[2px] mx-0.5 shrink-0 mt-4 ${isCompleted || isCurrent ? 'bg-emerald-400' : 'bg-slate-200'}`} />
              )}
              <div className="flex flex-col items-center min-w-[72px]">
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center transition-all
                  ${isCompleted ? 'bg-emerald-100 text-emerald-600' : ''}
                  ${isCurrent ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-300 ring-offset-1 animate-pulse' : ''}
                  ${isFuture ? 'bg-slate-100 text-slate-300' : ''}
                `}>
                  {isCompleted ? (
                    <CheckCircle size={18} weight="fill" />
                  ) : (
                    <StepIcon size={16} weight={isCurrent ? 'fill' : 'regular'} />
                  )}
                </div>
                <span className={`text-[10px] mt-1.5 font-bold text-center leading-tight ${
                  isCompleted ? 'text-emerald-600' : isCurrent ? 'text-blue-700' : 'text-slate-300'
                }`}>
                  {step.label}
                </span>
                {eventInfo && (
                  <span className="text-[9px] text-slate-400 mt-0.5">
                    {new Date(eventInfo.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
          );
        })}

        {/* Rejected branch */}
        {isRejected && (
          <div className="flex items-start">
            <div className="w-6 h-[2px] mx-0.5 shrink-0 bg-rose-300 mt-4" />
            <div className="flex flex-col items-center min-w-[72px]">
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-rose-100 text-rose-600 ring-2 ring-rose-300 ring-offset-1">
                <XCircle size={18} weight="fill" />
              </div>
              <span className="text-[10px] mt-1.5 font-bold text-center text-rose-600">Rejected</span>
              {eventMap['rejected'] && (
                <span className="text-[9px] text-slate-400 mt-0.5">
                  {new Date(eventMap['rejected'].timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
